import { useRef, useState, useCallback, useEffect } from 'react';

export function usePlayer() {
  const [frames, setFrames] = useState([]);
  const [fps, setFps] = useState(25);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [duration, setDuration] = useState(0);

  const tsArrayRef = useRef([]);
  const animationFrameIdRef = useRef(null);
  const startTimeRef = useRef(0);
  const seekTimeRef = useRef(0);
  const renderCallbackRef = useRef(null);

  const framesRef = useRef([]);
  const fpsRef = useRef(25);
  const isPlayingRef = useRef(false);
  const isEndedRef = useRef(false);
  const currentFrameIndexRef = useRef(0);
  const durationRef = useRef(0);

  const totalFrames = frames.length;

  const getFrameIndexByTime = useCallback((time) => {
    const tsArray = tsArrayRef.current;
    const len = tsArray.length;
    if (len === 0) return 0;
    if (time <= tsArray[0]) return 0;
    if (time >= tsArray[len - 1]) return len - 1;
    let lo = 0, hi = len - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (tsArray[mid] <= time) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }, []);

  const getFrameTs = useCallback((frameIndex) => {
    const tsArray = tsArrayRef.current;
    if (frameIndex < 0 || frameIndex >= tsArray.length) return 0;
    return tsArray[frameIndex];
  }, []);

  const renderFrame = useCallback((frameIndex, preciseTime) => {
    const currentFrames = framesRef.current;
    const currentDuration = durationRef.current;
    if (frameIndex < 0 || frameIndex >= currentFrames.length) return;
    setCurrentFrameIndex(frameIndex);
    currentFrameIndexRef.current = frameIndex;
    const t = preciseTime != null ? preciseTime : getFrameTs(frameIndex);
    const clamped = Math.max(0, Math.min(t, currentDuration));
    setCurrentTime(clamped);
    if (renderCallbackRef.current) {
      renderCallbackRef.current(currentFrames[frameIndex], frameIndex, clamped);
    }
  }, [getFrameTs]);

  const tick = useCallback((now) => {
    if (!isPlayingRef.current) return;
    const elapsed = (now - startTimeRef.current) / 1000 + seekTimeRef.current;
    const currentDuration = durationRef.current;
    const currentTotalFrames = framesRef.current.length;
    if (elapsed >= currentDuration) {
      renderFrame(currentTotalFrames - 1, currentDuration);
      setIsEnded(true);
      isEndedRef.current = true;
      setIsPlaying(false);
      isPlayingRef.current = false;
      animationFrameIdRef.current = null;
      return;
    }
    const frameIndex = getFrameIndexByTime(elapsed);
    renderFrame(frameIndex, elapsed);
    animationFrameIdRef.current = requestAnimationFrame(tick);
  }, [getFrameIndexByTime, renderFrame]);

  const setRenderCallback = useCallback((fn) => {
    renderCallbackRef.current = fn;
  }, []);

  const init = useCallback((frameData, frameRate, actualDuration) => {
    setFrames(frameData);
    framesRef.current = frameData;
    const rate = frameRate || 25;
    setFps(rate);
    fpsRef.current = rate;
    const ts = frameData.map((f) => f.ts ?? 0);
    tsArrayRef.current = ts;
    let dur;
    if (actualDuration != null && actualDuration > 0) {
      dur = actualDuration;
    } else if (ts.length > 0) {
      dur = ts[ts.length - 1];
    } else {
      dur = 0;
    }
    setDuration(dur);
    durationRef.current = dur;
    setCurrentFrameIndex(0);
    currentFrameIndexRef.current = 0;
    setCurrentTime(0);
    setIsEnded(false);
    isEndedRef.current = false;
    setIsPlaying(false);
    isPlayingRef.current = false;
    seekTimeRef.current = 0;
  }, []);

  const play = useCallback(() => {
    if (isPlayingRef.current || framesRef.current.length === 0) return;
    if (isEndedRef.current) {
      seekTimeRef.current = 0;
      setCurrentFrameIndex(0);
      currentFrameIndexRef.current = 0;
      setIsEnded(false);
      isEndedRef.current = false;
    }
    seekTimeRef.current = getFrameTs(currentFrameIndexRef.current);
    startTimeRef.current = performance.now();
    setIsPlaying(true);
    isPlayingRef.current = true;
    animationFrameIdRef.current = requestAnimationFrame(tick);
  }, [getFrameTs, tick]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) {
      pause();
    } else {
      play();
    }
  }, [pause, play]);

  const seekToTime = useCallback((time) => {
    if (framesRef.current.length === 0) return;
    const currentDuration = durationRef.current;
    const clampedTime = Math.max(0, Math.min(time, currentDuration));
    const frameIndex = getFrameIndexByTime(clampedTime);
    seekTimeRef.current = clampedTime;
    startTimeRef.current = performance.now();
    renderFrame(frameIndex, clampedTime);
  }, [getFrameIndexByTime, renderFrame]);

  const seekToFrame = useCallback((frameIndex) => {
    seekToTime(getFrameTs(frameIndex));
  }, [getFrameTs, seekToTime]);

  const stop = useCallback(() => {
    pause();
    setIsEnded(false);
    isEndedRef.current = false;
    seekTimeRef.current = 0;
    const tsArray = tsArrayRef.current;
    renderFrame(0, tsArray[0] ?? 0);
  }, [pause, renderFrame]);

  const replay = useCallback(() => {
    pause();
    setIsEnded(false);
    isEndedRef.current = false;
    seekTimeRef.current = 0;
    const tsArray = tsArrayRef.current;
    renderFrame(0, tsArray[0] ?? 0);
    requestAnimationFrame(() => { play(); });
  }, [pause, renderFrame, play]);

  useEffect(() => {
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, []);

  return {
    frames,
    fps,
    isPlaying,
    isEnded,
    currentTime,
    currentFrameIndex,
    totalFrames,
    duration,
    init,
    setRenderCallback,
    renderFrame,
    play,
    pause,
    togglePlay,
    seekToTime,
    seekToFrame,
    stop,
    replay,
    getFrameIndexByTime,
  };
}
